import { Link } from "react-router-dom";
import { Leaf, Waves, ExternalLink, Globe, Facebook } from "lucide-react";
import EtwinningBadge from "@/components/EtwinningBadge";
import { useT } from "@/lib/i18n";

const FACEBOOK_GROUP_URL =
    "https://www.facebook.com/groups/1238118641469083/?ref=share&mibextid=NSMWBT";

export default function Footer({ project }) {
    const { t } = useT();
    const etwinningUrl = project?.etwinning_url;
    const siteName = project?.site_name || "stemcycle-g2b-green-to-blue-innovation-lab.com";
    const siteDomain = project?.site_domain || "https://stemcycle-g2b-green-to-blue-innovation-lab.com/";

    return (
        <footer className="relative mt-32 border-t border-black/5 bg-bg-alt/40">
            <div className="container-pad py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
                <div className="md:col-span-5">
                    <div className="flex items-center gap-3 mb-5 flex-wrap">
                        <img
                            src="/images/stemcycle-g2b-logo.jpg"
                            alt="STEMcycle G2B"
                            className="w-12 h-12 rounded-full object-cover ring-1 ring-black/5"
                        />
                        <EtwinningBadge href={etwinningUrl} />
                    </div>
                    <div className="flex items-center gap-3 mb-4">
                        <Leaf className="text-brand-green" size={22} strokeWidth={1.5} />
                        <span className="text-ink-muted text-xs uppercase tracking-[0.2em]">
                            from forest
                        </span>
                        <span className="h-[1px] flex-1 bg-gradient-to-r from-brand-green to-brand-blue" />
                        <span className="text-ink-muted text-xs uppercase tracking-[0.2em]">
                            to ocean
                        </span>
                        <Waves className="text-brand-blue" size={22} strokeWidth={1.5} />
                    </div>
                    <h3 className="heading-serif text-3xl md:text-4xl text-ink-primary text-balance">
                        Green Acts. <span className="gradient-g2b-text">Blue Futures.</span>
                    </h3>
                    <p className="mt-4 text-ink-secondary leading-relaxed max-w-md text-[16px]">
                        Co-founded by <strong>Istanbul TOKİ Kayaşehir MTAL 🇹🇷</strong> and{" "}
                        <strong>Liceul Teoretic „Aurel Vlaicu" Breaza 🇷🇴</strong>, with partner
                        schools in Italy 🇮🇹, Republic of Moldova 🇲🇩, Georgia 🇬🇪, Lithuania 🇱🇹 and
                        Bosnia &amp; Herzegovina 🇧🇦.
                    </p>

                    <a
                        href={FACEBOOK_GROUP_URL}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-testid="footer-facebook-link"
                        className="mt-6 inline-flex items-center gap-2.5 bg-[#1877F2] hover:bg-[#0c5dca] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200"
                    >
                        <Facebook size={16} strokeWidth={2} fill="currentColor" />
                        {t("common.joinFacebook")}
                    </a>

                    <a
                        href={siteDomain}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-testid="footer-site-domain"
                        className="mt-6 ml-0 inline-flex items-center gap-2 group"
                    >
                        <span className="inline-flex w-9 h-9 items-center justify-center rounded-full gradient-g2b text-white">
                            <Globe size={16} strokeWidth={1.6} />
                        </span>
                        <span className="font-mono text-[13px] text-ink-primary group-hover:text-brand-blue transition-colors break-all">
                            {siteName}
                        </span>
                    </a>
                </div>

                <div className="md:col-span-3 md:col-start-7">
                    <div className="caption mb-4">{t("nav.worklines")}</div>
                    <ul className="space-y-2 text-[15px] text-ink-secondary">
                        <li><Link className="hover:text-brand-green" to="/theme/mountain-day">Mountain Day · Dec</Link></li>
                        <li><Link className="hover:text-brand-green" to="/theme/green-to-blue">Green to Blue · Dec</Link></li>
                        <li><Link className="hover:text-brand-green" to="/theme/january-mission">Mind Map · Jan</Link></li>
                        <li><Link className="hover:text-brand-green" to="/theme/safer-internet">Safer Internet · Feb</Link></li>
                        <li><Link className="hover:text-brand-green" to="/theme/april-earth">Earth Day & Blue Research · Apr</Link></li>
                        <li>
                            <Link
                                className="inline-flex items-center gap-1 text-brand-gold font-semibold hover:text-brand-blue"
                                to="/theme/may-etwinning"
                            >
                                ★ eTwinning Day · May
                            </Link>
                        </li>
                    </ul>
                </div>

                <div className="md:col-span-3">
                    <div className="caption mb-4">{t("nav.more")}</div>
                    <ul className="space-y-2 text-[15px] text-ink-secondary">
                        <li><Link className="hover:text-brand-green" to="/theme/renewable-energy">Renewable Energy</Link></li>
                        <li><Link className="hover:text-brand-green" to="/theme/new-year-wishes">New Year Wishes</Link></li>
                        <li><Link className="hover:text-brand-green" to="/theme/introductions">Student Voices</Link></li>
                        <li><Link className="hover:text-brand-green" to="/schools">{t("nav.schools")}</Link></li>
                        <li><Link className="hover:text-brand-green" to="/about">{t("nav.about")}</Link></li>
                        {etwinningUrl && (
                            <li>
                                <a
                                    href={etwinningUrl}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="inline-flex items-center gap-1 hover:text-brand-green"
                                >
                                    eTwinning <ExternalLink size={12} strokeWidth={1.5} />
                                </a>
                            </li>
                        )}
                    </ul>
                </div>
            </div>

            <div className="border-t border-black/5">
                <div className="container-pad py-6 flex flex-col md:flex-row items-start md:items-center justify-between text-xs text-ink-muted gap-3">
                    <span>© {new Date().getFullYear()} {siteName} — A student showcase.</span>
                    <span className="font-mono">#6DECEMBER · #7JANUARY · #8FEBRUARY · #9APRIL · #10MAY · #G2B</span>
                </div>
            </div>
        </footer>
    );
}
